import React from 'react';
import { motion } from 'framer-motion';
import { eventPhotos } from '../data/plansData';

const EventGallery = () => {
  return (
    <section 
      id="events"
      className="relative py-24 md:py-32 overflow-hidden"
      data-testid="event-gallery"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-surface-dark" />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-neon-green/40 bg-neon-green/10 text-neon-green text-sm font-semibold mb-6">
            Momentos Reais
          </span>
          <h2 className="font-outfit font-black text-3xl sm:text-4xl lg:text-5xl text-white mb-4">
            O que acontece{' '}
            <span className="gradient-text-green">quando pessoas reais se conectam</span>
          </h2>
          <p className="text-white/70 text-lg max-w-3xl mx-auto">
            Combinamos o digital com o presencial, criando pontes entre o online e encontros reais,
            onde <span className="text-gold font-semibold">confiança é construída, reputação é validada e oportunidades acontecem de verdade</span>.
          </p>
        </motion.div>

        {/* Gallery Grid - Bento layout */}
        <div className="grid grid-cols-12 gap-4 auto-rows-[200px]">
          {/* Large image - main event */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="col-span-12 md:col-span-8 md:row-span-2 relative rounded-2xl overflow-hidden group cursor-pointer"
            whileHover={{ scale: 1.02 }}
          >
            <img
              src={eventPhotos[0].url}
              alt={eventPhotos[0].caption}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <span className="inline-block px-3 py-1 bg-neon-green/20 border border-neon-green/50 text-neon-green text-xs font-bold uppercase tracking-wider rounded-full mb-2">
                AI Brasil Experience
              </span>
              <h3 className="font-outfit font-black text-2xl text-white">
                {eventPhotos[0].caption}
              </h3>
            </div>
          </motion.div>

          {/* Medium images */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="col-span-6 md:col-span-4 relative rounded-2xl overflow-hidden group cursor-pointer"
            whileHover={{ scale: 1.02 }}
          >
            <img
              src={eventPhotos[1].url}
              alt={eventPhotos[1].caption}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-4">
              <p className="text-white font-semibold text-sm">
                {eventPhotos[1].caption}
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="col-span-6 md:col-span-4 relative rounded-2xl overflow-hidden group cursor-pointer"
            whileHover={{ scale: 1.02 }}
          >
            <img
              src={eventPhotos[2].url}
              alt={eventPhotos[2].caption}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-4">
              <p className="text-white font-semibold text-sm">
                {eventPhotos[2].caption}
              </p>
            </div>
          </motion.div>

          {/* Bottom row */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="col-span-6 md:col-span-6 relative rounded-2xl overflow-hidden group cursor-pointer"
            whileHover={{ scale: 1.02 }}
          >
            <img
              src={eventPhotos[3].url}
              alt={eventPhotos[3].caption}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-4">
              <p className="text-white font-semibold text-sm">
                {eventPhotos[3].caption}
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="col-span-6 md:col-span-6 relative rounded-2xl overflow-hidden group cursor-pointer"
            whileHover={{ scale: 1.02 }}
          >
            <img
              src={eventPhotos[4].url}
              alt={eventPhotos[4].caption}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-4">
              <p className="text-white font-semibold text-sm">
                {eventPhotos[4].caption}
              </p>
            </div>
          </motion.div>
        </div>

        {/* Quote */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 max-w-4xl mx-auto text-center"
        >
          <p className="text-2xl sm:text-3xl font-outfit text-white/90 leading-relaxed">
            "No Hub, cada interação importa. Cada contribuição gera{' '}
            <span className="gradient-text-green font-bold">reconhecimento</span>. Cada conexão pode se
            transformar em <span className="gradient-text-gold font-bold">negócio, parceria ou crescimento real</span>."
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default EventGallery;
